// Canvas game engine for Crossbow Chaos
// Handles spawn/motion/collision/draw. Pure JS so we can use requestAnimationFrame efficiently.
import {
  TARGETS, ASSETS, SKULL_TARGET, UNIQUE_TARGET_IDS, TROPHY_TARGET,
  ARROW_PACK_TARGET, PINATA_TARGET, COIN_PICKUP_TARGET,
  MAGNET_TARGET, LIGHTNING_TARGET, WHEEL_TARGET,
} from "@/data/catalog";

// Canvas emoji font stack — `serif` (the previous default) doesn't include
// emoji glyphs on iOS / older Android WebViews, so emoji-only fallback
// targets rendered as nothing on mobile. Putting platform emoji fonts first
// makes the fallback path safe on every device.
const EMOJI_FONT = '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
import { audio } from "@/game/audio";

// Simple HTMLImageElement cache. Asset PNGs are already transparent-
// background and sized for the UI; just load and draw.
const imageCache = {};

function getImage(src) {
  if (!src) return null;
  const cached = imageCache[src];
  if (cached) return cached;
  if (cached === undefined) {
    imageCache[src] = null; // loading
    const img = new Image();
    img.onload = () => { imageCache[src] = img; };
    img.src = src;
  }
  return null;
}

// warm-cache all assets
Object.values(ASSETS).forEach((s) => { if (s) getImage(s); });

function randRange(a, b) { return a + Math.random() * (b - a); }
function choice(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

export class Particle {
  constructor(x, y, color = "#FFD700", opts = {}) {
    this.x = x; this.y = y;
    this.vx = opts.vx ?? randRange(-3, 3);
    this.vy = opts.vy ?? randRange(-6, -1);
    this.gravity = opts.gravity ?? 0.25;
    this.life = opts.life ?? 40;
    this.age = 0;
    this.color = color;
    this.size = opts.size ?? 6;
    this.text = opts.text;
  }
  update() {
    this.x += this.vx;
    this.y += this.vy;
    this.vy += this.gravity;
    this.age++;
  }
  draw(ctx) {
    const alpha = Math.max(0, 1 - this.age / this.life);
    const prevAlpha = ctx.globalAlpha;
    ctx.globalAlpha = alpha;
    if (this.text) {
      // Text labels are sparse enough to afford a glow.
      ctx.save();
      ctx.fillStyle = this.color;
      ctx.shadowBlur = 10;
      ctx.shadowColor = this.color;
      ctx.font = "bold 20px Fredoka, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(this.text, this.x, this.y);
      ctx.restore();
    } else {
      // Hot path: particle bursts can hit hundreds on screen; no shadow.
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = prevAlpha;
  }
  get dead() { return this.age >= this.life; }
}

export class Arrow {
  constructor(x, y, vx, vy, color = "#E0E0E0", glow = "#FFFFFF", rainbow = false) {
    this.x = x; this.y = y; this.vx = vx; this.vy = vy;
    this.color = color; this.glow = glow; this.rainbow = rainbow;
    this.age = 0;
    // Bouncy Arrows mode sets bouncesLeft > 0 so the arrow ricochets off the
    // arena walls a fixed number of times before flying off-screen.
    this.bouncesLeft = 0;
  }
  update() { this.x += this.vx; this.y += this.vy; this.age++; }
  // Reflect off arena walls if any bounces remain. Caller decides when to
  // invoke (after move + before collision). Returns true if a bounce
  // happened this frame.
  tryBounce(w, h) {
    if (this.bouncesLeft <= 0) return false;
    let bounced = false;
    if (this.x <= 4 && this.vx < 0) { this.x = 4; this.vx = -this.vx; bounced = true; }
    else if (this.x >= w - 4 && this.vx > 0) { this.x = w - 4; this.vx = -this.vx; bounced = true; }
    if (this.y <= 4 && this.vy < 0) { this.y = 4; this.vy = -this.vy; bounced = true; }
    else if (this.y >= h - 4 && this.vy > 0) { this.y = h - 4; this.vy = -this.vy; bounced = true; }
    if (bounced) this.bouncesLeft -= 1;
    return bounced;
  }
  draw(ctx) {
    ctx.save();
    const angle = Math.atan2(this.vy, this.vx);
    ctx.translate(this.x, this.y);
    ctx.rotate(angle);
    let color = this.color;
    if (this.rainbow) {
      const hues = ["#FF007F", "#FFD700", "#39FF14", "#00FFFF", "#A020F0"];
      color = hues[Math.floor(this.age / 3) % hues.length];
    }
    ctx.shadowBlur = 8;
    ctx.shadowColor = this.glow;
    ctx.fillStyle = color;
    ctx.fillRect(-22, -2, 40, 4);
    // tip
    ctx.beginPath();
    ctx.moveTo(22, 0); ctx.lineTo(14, -5); ctx.lineTo(14, 5); ctx.closePath();
    ctx.fill();
    // fletching
    ctx.fillStyle = "#FF007F";
    ctx.beginPath();
    ctx.moveTo(-22, 0); ctx.lineTo(-28, -5); ctx.lineTo(-28, 5); ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  offScreen(w, h) { return this.x < -60 || this.x > w + 60 || this.y < -60 || this.y > h + 60; }
}

export class Target {
  constructor({ def, x, y, vx, vy, speedMul = 1, bossHp = null }) {
    this.def = def;
    this.x = x; this.y = y;
    this.vx = vx * speedMul;
    this.vy = vy * speedMul;
    this.size = def.size;
    this.rotation = 0;
    this.rotSpeed = def.motion === "spin" ? randRange(0.03, 0.08) * (Math.random() < 0.5 ? -1 : 1) : 0;
    this.motion = def.motion;
    this.phase = Math.random() * Math.PI * 2;
    this.baseY = y;
    // Zigzag parameters — overridable per-target so waves can be varied/layered.
    this.zigAmplitude = def.zigAmplitude ?? 70;
    this.zigFrequency = def.zigFrequency ?? 0.05;
    this.zigAmp2 = def.zigAmp2 ?? 0;
    this.zigFreq2 = def.zigFreq2 ?? 0;
    this.age = 0;
    this.dead = false;
    this.hp = bossHp; // for boss
    this.maxHp = bossHp;
    this.speedMul = speedMul;
    // Mini-game extension fields. Cheap to carry on every Target so spawners
    // can opt in by setting them post-construction without subclassing.
    this.startSize = def.size;
    this.shrinkPerTick = def.shrinkPerTick ?? 0;
    this.minSize = def.minSize ?? 18;
    this.lifespan = def.lifespan ?? null;
    this.tintGlow = def.tintGlow ?? null;
    this.colorState = def.colorState ?? null;
  }

  update(w, h) {
    this.age++;
    // Optional shrink (Shrinking Stars) — when below minSize the target
    // expires on its own so the screen never fills with invisible specks.
    if (this.shrinkPerTick > 0) {
      this.size = Math.max(this.minSize, this.size - this.shrinkPerTick);
      if (this.size <= this.minSize) this.dead = true;
    }
    // Optional self-destruct after a fixed lifespan (Mole Mash, etc.).
    if (this.lifespan !== null && this.age >= this.lifespan) {
      this.dead = true;
      return;
    }
    if (this.motion === "linear") {
      this.x += this.vx; this.y += this.vy;
    } else if (this.motion === "bounce") {
      this.x += this.vx; this.y += this.vy;
      if (this.y > h - this.size / 2) {
        this.y = h - this.size / 2; this.vy = -Math.abs(this.vy) * 0.9;
      }
      this.vy += 0.25 * this.speedMul;
    } else if (this.motion === "spin") {
      this.x += this.vx; this.y += this.vy;
      this.rotation += this.rotSpeed;
    } else if (this.motion === "zigzag") {
      this.x += this.vx;
      const t = this.age + this.phase * 20;
      let yOffset = Math.sin(t * this.zigFrequency) * this.zigAmplitude;
      if (this.zigAmp2) yOffset += Math.sin(t * this.zigFreq2) * this.zigAmp2;
      this.y = this.baseY + yOffset;
    } else if (this.motion === "rocket") {
      this.x += this.vx * 1.6;
      this.y += Math.sin(this.age * 0.08) * 2;
    }

    // Wall bounce — apply after the target has fully entered the arena.
    // Rocket targets still fly off for fun. canFall targets (boss-dropped
    // skulls, etc.) pass right through the bottom so they can hit the ground.
    if (this.motion !== "rocket" && !this.canFall) {
      const pad = this.size / 2;
      if (!this.hasEntered && this.x > pad + 2 && this.x < w - pad - 2) {
        this.hasEntered = true;
      }
      if (this.hasEntered) {
        // left/right
        if (this.x < pad) {
          this.x = pad;
          this.vx = Math.abs(this.vx);
          if (this.motion === "zigzag") this.baseY = this.y;
        } else if (this.x > w - pad) {
          this.x = w - pad;
          this.vx = -Math.abs(this.vx);
          if (this.motion === "zigzag") this.baseY = this.y;
        }
        // top wall
        if (this.y < pad) {
          this.y = pad;
          this.vy = Math.abs(this.vy || 1);
          if (this.motion === "zigzag") this.baseY = this.y;
        }
        // bottom wall — non-bounce motions. Leave 120px of clearance so the
        // crossbow + skin at the bottom stay free of targets.
        const bottomPad = 120;
        if (this.motion !== "bounce" && this.y > h - pad - bottomPad) {
          this.y = h - pad - bottomPad;
          this.vy = -Math.abs(this.vy || 1);
          if (this.motion === "zigzag") this.baseY = this.y;
        }
      }
    }
  }

  draw(ctx) {
    const img = getImage(ASSETS[this.def.id]);
    ctx.save();
    ctx.translate(this.x, this.y);
    if (this.rotation) ctx.rotate(this.rotation);
    // Color-state tint ring (Color Match): solid colored disc behind the
    // sprite so the player can read the rule at a glance.
    if (this.tintGlow) {
      ctx.save();
      ctx.shadowBlur = 24;
      ctx.shadowColor = this.tintGlow;
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = this.tintGlow;
      ctx.beginPath();
      ctx.arc(0, 0, this.size / 2 + 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    // glow ring for difficulty
    const glow =
      this.def.difficulty === "skull"   ? "#FF3B30" :
      this.def.difficulty === "trophy"  ? "#FFD700" :
      this.def.difficulty === "pack"    ? "#00FFFF" :
      this.def.difficulty === "hard"    ? "#FF3B30" :
      this.def.difficulty === "medium"  ? "#FFD700" : "#00FFFF";
    ctx.shadowBlur =
      this.def.difficulty === "skull" || this.def.difficulty === "trophy" || this.def.difficulty === "pack"
        ? 20
        : 12;
    ctx.shadowColor = glow;
    if (img) {
      ctx.drawImage(img, -this.size / 2, -this.size / 2, this.size, this.size);
    } else if (this.def.emoji) {
      ctx.font = `${Math.floor(this.size * 0.78)}px ${EMOJI_FONT}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(this.def.emoji, 0, 0);
    } else {
      // fallback circle
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // HP bar for boss
    if (this.hp !== null && this.maxHp) {
      const w = this.size * 1.2;
      const pct = this.hp / this.maxHp;
      ctx.save();
      ctx.translate(this.x, this.y - this.size / 2 - 16);
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.fillRect(-w / 2, 0, w, 8);
      const c = pct > 0.5 ? "#39FF14" : pct > 0.25 ? "#FFD700" : "#FF3B30";
      ctx.fillStyle = c;
      ctx.shadowBlur = 10;
      ctx.shadowColor = c;
      ctx.fillRect(-w / 2, 0, w * pct, 8);
      ctx.restore();
    }
  }

  hitTest(px, py) {
    const dx = px - this.x, dy = py - this.y;
    return (dx * dx + dy * dy) <= (this.size / 2) * (this.size / 2);
  }

  offScreen(w, h) {
    // Targets bounce now — only rocket motion is meant to leave the screen.
    // Let bouncy/non-rocket targets stay; only remove if they fell off bottom
    // or are a rocket that exited. For rain, they should drop off bottom.
    if (this.motion === "rocket") {
      const m = this.size;
      return this.x < -m || this.x > w + m;
    }
    // canFall targets bypass wall bouncing, so they can exit any side.
    if (this.canFall) {
      const m = this.size;
      return this.y > h + m || this.y < -m || this.x < -m || this.x > w + m;
    }
    return this.y > h + this.size;
  }
}

// Boss-drop skull hit ground?
export function hitGround(target, h) {
  return target.canFall && target.y > h - 20;
}

// ---- Wall (Wall Breaker mini-game) --------------------------------------
// Rectangular destructible obstacle. Blocks arrows until its HP reaches 0.
// An attached `childTarget` rides along with it and is unreachable while the
// wall is alive.
export class Wall {
  constructor(x, y, vx, vy, opts = {}) {
    this.x = x; this.y = y;
    this.vx = vx; this.vy = vy;
    this.w = opts.w || 110;
    this.h = opts.h || 170;
    this.hp = opts.hp || 3;
    this.maxHp = this.hp;
    this.dead = false;
    this.childTarget = null;
    this.age = 0;
    this.color = opts.color || "#FF6A00";
  }
  update(sw, sh) {
    this.age++;
    this.x += this.vx;
    this.y += this.vy;
    const padX = this.w / 2 + 6;
    const topPad = this.h / 2 + 60;
    const bottomPad = this.h / 2 + 140; // keep clear of HUD + crossbow
    if (this.x < padX) { this.x = padX; this.vx = Math.abs(this.vx); }
    if (this.x > sw - padX) { this.x = sw - padX; this.vx = -Math.abs(this.vx); }
    if (this.y < topPad) { this.y = topPad; this.vy = Math.abs(this.vy); }
    if (this.y > sh - bottomPad) { this.y = sh - bottomPad; this.vy = -Math.abs(this.vy); }
    if (this.childTarget && !this.childTarget.dead) {
      this.childTarget.x = this.x;
      this.childTarget.y = this.y;
    }
  }
  hitTest(px, py) {
    return Math.abs(px - this.x) <= this.w / 2 && Math.abs(py - this.y) <= this.h / 2;
  }
  draw(ctx) {
    const pct = Math.max(0, this.hp) / this.maxHp;
    const color = pct > 0.66 ? "#39FF14" : pct > 0.33 ? "#FFD700" : "#FF3B30";
    ctx.save();
    // brick body
    ctx.shadowColor = color;
    ctx.shadowBlur = 24;
    ctx.fillStyle = "rgba(11,12,16,0.92)";
    ctx.fillRect(this.x - this.w / 2, this.y - this.h / 2, this.w, this.h);
    // neon border
    ctx.strokeStyle = color;
    ctx.lineWidth = 4;
    ctx.strokeRect(this.x - this.w / 2, this.y - this.h / 2, this.w, this.h);
    // brick grid — static pattern so it doesn't shimmer
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    const rows = 4;
    const cols = 3;
    for (let r = 1; r < rows; r++) {
      const y = this.y - this.h / 2 + (this.h / rows) * r;
      ctx.beginPath();
      ctx.moveTo(this.x - this.w / 2, y);
      ctx.lineTo(this.x + this.w / 2, y);
      ctx.stroke();
    }
    for (let c = 1; c < cols; c++) {
      const x = this.x - this.w / 2 + (this.w / cols) * c;
      ctx.beginPath();
      ctx.moveTo(x, this.y - this.h / 2);
      ctx.lineTo(x, this.y + this.h / 2);
      ctx.stroke();
    }
    // deterministic crack pattern based on damage — seeded so it doesn't jitter
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    const cracks = this.maxHp - this.hp;
    for (let i = 0; i < cracks; i++) {
      const seed = (i + 1) * 37;
      const sx = this.x - this.w / 2 + ((seed * 13) % this.w);
      const sy = this.y - this.h / 2 + 6;
      const ex = this.x - this.w / 2 + ((seed * 29) % this.w);
      const ey = this.y + this.h / 2 - 6;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo((sx + ex) / 2 + 6, (sy + ey) / 2);
      ctx.lineTo(ex, ey);
      ctx.stroke();
    }
    // HP pips above the wall
    ctx.globalAlpha = 1;
    const pipW = 10, pipGap = 4;
    const totalW = this.maxHp * pipW + (this.maxHp - 1) * pipGap;
    const startX = this.x - totalW / 2;
    const pipY = this.y - this.h / 2 - 14;
    for (let i = 0; i < this.maxHp; i++) {
      ctx.fillStyle = i < this.hp ? color : "rgba(255,255,255,0.15)";
      ctx.shadowColor = color;
      ctx.shadowBlur = i < this.hp ? 8 : 0;
      ctx.fillRect(startX + i * (pipW + pipGap), pipY, pipW, 4);
    }
    ctx.restore();
  }
}

// Spawn a wall + hidden target that move together. When the wall breaks,
// caller should release the child (clear hiddenBy and give it its own vel).
export function spawnWallWithTarget(sw, sh) {
  const fromLeft = Math.random() < 0.5;
  const vx = (fromLeft ? 1 : -1) * randRange(1.4, 2.2);
  const vy = randRange(-0.8, 0.8);
  const y = randRange(sh * 0.25, sh * 0.55);
  const x = fromLeft ? 120 : sw - 120;
  const wall = new Wall(x, y, vx, vy, { hp: 3 });

  // Attached target — any non-skull target. Start at same position as wall.
  const def = { ...choice(TARGETS.filter((t) => t.difficulty !== "skull")), motion: "linear" };
  const target = new Target({ def, x, y, vx: 0, vy: 0, speedMul: 1 });
  target.hiddenBy = wall;
  wall.childTarget = target;
  return { wall, target };
}

export function spawnTarget(w, h, level, mode = "classic") {
  // Classic mode: ~12% chance to spawn a cursed skull.
  if (mode === "classic" && Math.random() < 0.12) {
    return spawnSkull(w, h, level);
  }
  const pool = TARGETS.filter((t) => t.minLevel <= level);
  const def = choice(pool);
  const speedMul = 1 + (level - 1) * 0.15;
  const fromLeft = Math.random() < 0.5;
  let x, y, vx, vy;
  if (mode === "rain") {
    x = randRange(80, w - 80);
    y = -60;
    vx = randRange(-0.5, 0.5);
    vy = randRange(2.2, 3.5) * speedMul;
  } else {
    x = fromLeft ? -60 : w + 60;
    y = randRange(h * 0.15, h * 0.55);
    vx = (fromLeft ? 1 : -1) * randRange(2.2, 3.6) * speedMul;
    vy = def.motion === "bounce" ? randRange(-5, -2) : randRange(-0.5, 0.5);
  }
  const t = new Target({ def, x, y, vx, vy, speedMul });
  // Rain drops fall straight through and get popped when they reach the
  // ground — no wall bouncing.
  if (mode === "rain") t.canFall = true;
  return t;
}

export function spawnSkull(w, h, level = 1) {
  const speedMul = 1 + (level - 1) * 0.1;
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.4, 3.6) * speedMul;
  return new Target({ def: { ...SKULL_TARGET }, x, y, vx, vy: randRange(-0.6, 0.6), speedMul });
}

// Hit 'em all spawner: prefer targets NOT yet hit. If all have been hit,
// still return a random one so duplicates can mercilessly zero the score.
export function spawnForHitEmAll(w, h, alreadyHit) {
  const pending = UNIQUE_TARGET_IDS.filter((id) => !alreadyHit.has(id));
  const pool = pending.length > 0 ? pending : UNIQUE_TARGET_IDS;
  const id = choice(pool);
  const def = TARGETS.find((t) => t.id === id) || TARGETS[0];
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.18, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.6, 3.8);
  const vy = def.motion === "bounce" ? randRange(-5, -2) : randRange(-0.3, 0.3);
  return new Target({ def: { ...def }, x, y, vx, vy, speedMul: 1 });
}

export function spawnTinyFast(w, h) {
  const def = { ...choice(TARGETS.filter((t) => t.minLevel <= 2)) };
  def.size = 50;
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.15, h * 0.7);
  const vx = (fromLeft ? 1 : -1) * randRange(6, 9);
  return new Target({ def, x, y, vx, vy: randRange(-0.3, 0.3), speedMul: 1 });
}

export function spawnZigzag(w, h) {
  // Per-target amplitude and frequency so each wave is visibly different,
  // optionally layered with a second harmonic for a more "wobbly" path.
  const amp = randRange(70, 170);
  const freq = randRange(0.025, 0.075);
  const layered = Math.random() < 0.55;
  const def = {
    ...choice(TARGETS),
    motion: "zigzag",
    zigAmplitude: amp,
    zigFrequency: freq,
    zigAmp2: layered ? randRange(18, 45) : 0,
    zigFreq2: layered ? randRange(0.12, 0.22) : 0,
  };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  // Keep the target's base Y away from the top/bottom so the wider waves
  // don't immediately smack the walls.
  const y = randRange(h * 0.3, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.4, 3.6);
  return new Target({ def, x, y, vx, vy: 0, speedMul: 1.1 });
}

export function spawnBoss(w, h, opts = {}) {
  const pool = TARGETS.filter((t) => t.difficulty === "hard");
  const def = { ...(opts.def || choice(pool)), size: opts.size || 200, motion: "linear" };
  const x = opts.x ?? w / 2;
  const y = opts.y ?? h * 0.35;
  const vx = opts.vx ?? (Math.random() < 0.5 ? 2.2 : -2.2);
  const vy = opts.vy ?? 1.2;
  const boss = new Target({
    def, x, y, vx, vy, speedMul: 1, bossHp: opts.hp || 14,
  });
  boss.isBoss = true;
  boss.phase = 1;
  boss.baseSize = def.size;
  boss.minSize = Math.max(70, def.size * 0.35);
  boss.bossId = opts.bossId || `boss_${Math.random().toString(36).slice(2, 7)}`;
  boss.lastDropAt = 0;
  return boss;
}

// Spawn two bosses at staggered horizontal positions, each with a different
// sprite when possible. They roam and bounce around the arena.
export function spawnBossPair(w, h) {
  const pool = TARGETS.filter((t) => t.difficulty === "hard");
  const a = choice(pool);
  let b = choice(pool);
  if (pool.length > 1) {
    while (b.id === a.id) b = choice(pool);
  }
  return [
    spawnBoss(w, h, {
      def: a, x: w * 0.3, y: h * 0.3, vx: 2.4, vy: 1.0, hp: 12, bossId: "boss_a",
    }),
    spawnBoss(w, h, {
      def: b, x: w * 0.7, y: h * 0.4, vx: -2.6, vy: 1.2, hp: 12, bossId: "boss_b",
    }),
  ];
}

// A skull that a boss drops — falls straight down and slips past the normal
// bottom padding so it can smack the ground.
export function spawnBossDropSkull(boss) {
  const def = { ...SKULL_TARGET, size: 70 };
  const skull = new Target({
    def, x: boss.x, y: boss.y + boss.size / 2,
    vx: randRange(-0.6, 0.6), vy: randRange(2.2, 3.2), speedMul: 1,
  });
  skull.canFall = true;
  skull.isBossDrop = true;
  skull.parentBossId = boss.bossId;
  return skull;
}

// Smoothly scale a boss's size based on current HP (hit-by-hit shrinking).
export function applyBossShrink(boss) {
  if (!boss || !boss.isBoss || !boss.maxHp) return;
  const pct = Math.max(0, boss.hp) / boss.maxHp;
  const next = boss.minSize + (boss.baseSize - boss.minSize) * pct;
  boss.size = next;
  if (boss.def) boss.def.size = next;
}

// ---- Mini-game spawners ---------------------------------------------------

// Balloon Pop: targets rise smoothly in a straight line from below the arena
// and drift up through the top if the player misses them.
export function spawnBalloon(w, h) {
  const def = { ...choice(TARGETS), motion: "linear" };
  const x = randRange(70, w - 70);
  const y = h + 60;
  const t = new Target({
    def, x, y,
    vx: randRange(-0.25, 0.25),
    vy: -randRange(1.9, 2.6),
    speedMul: 1,
  });
  t.canFall = true; // lets it exit the top cleanly (no wall bounce)
  return t;
}

// Giant Hunt: oversized slow targets.
export function spawnGiant(w, h) {
  const base = { ...choice(TARGETS) };
  const def = { ...base, size: Math.min(180, base.size * 1.8) };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -80 : w + 80;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(1.2, 1.8);
  const vy = def.motion === "bounce" ? randRange(-3, -1.5) : randRange(-0.2, 0.2);
  return new Target({ def, x, y, vx, vy, speedMul: 1 });
}

// Spawn a specific target id — used by themed modes (chicken/donut/rocket).
export function spawnOfType(w, h, id) {
  const base = TARGETS.find((t) => t.id === id);
  if (!base) return spawnTarget(w, h, 2);
  const def = { ...base };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.15, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.4, 3.8);
  const vy = def.motion === "bounce" ? randRange(-5, -2) : randRange(-0.4, 0.4);
  return new Target({ def, x, y, vx, vy, speedMul: 1 });
}

// Frozen Time: any target, slowed way down.
export function spawnFrozen(w, h) {
  const def = { ...choice(TARGETS) };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.18, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(0.9, 1.5);
  const vy = def.motion === "bounce" ? randRange(-2.5, -1) : randRange(-0.2, 0.2);
  return new Target({ def, x, y, vx, vy, speedMul: 0.45 });
}

// Speed Demon: a target that flies in from a random edge (top, bottom, left,
// or right) and passes straight through the arena.
export function spawnFromAnySide(w, h, level = 3) {
  const pool = TARGETS.filter((t) => t.minLevel <= level);
  const base = choice(pool);
  const def = { ...base, motion: "linear" };
  const side = Math.floor(Math.random() * 4);
  const speed = randRange(4.2, 6.2);
  let x, y, vx, vy;
  if (side === 0) { // left → right
    x = -60; y = randRange(h * 0.12, h * 0.78);
    vx = speed; vy = randRange(-1.2, 1.2);
  } else if (side === 1) { // right → left
    x = w + 60; y = randRange(h * 0.12, h * 0.78);
    vx = -speed; vy = randRange(-1.2, 1.2);
  } else if (side === 2) { // top → bottom
    x = randRange(90, w - 90); y = -60;
    vx = randRange(-1.2, 1.2); vy = speed;
  } else { // bottom → top
    x = randRange(90, w - 90); y = h + 60;
    vx = randRange(-1.2, 1.2); vy = -speed;
  }
  const t = new Target({ def, x, y, vx, vy, speedMul: 1 });
  t.canFall = true; // no wall bouncing — it flies through and gets cleaned up
  return t;
}

// Trophy Trial skull: clusters tightly around the trophy so the trophy is
// surrounded by skulls and the player has to thread a real gap.
export function spawnTrophyTrialSkull(w, h, trophy) {
  if (!trophy) return spawnSkull(w, h, 1);
  const fromLeft = Math.random() < 0.5;
  const offset = randRange(60, 140);
  const x = trophy.x + (fromLeft ? -offset : offset);
  // Bias slightly below the trophy so the skulls sit between the player
  // (bottom of arena) and the trophy, directly in the shooting lane.
  const y = trophy.y + randRange(-25, 55);
  const vx = (fromLeft ? 1 : -1) * randRange(1.0, 1.8);
  return new Target({
    def: { ...SKULL_TARGET, size: 80 },
    x, y, vx, vy: randRange(-0.2, 0.2), speedMul: 1,
  });
}

// Bounce House: all bouncy.
export function spawnBouncer(w, h) {
  const base = { ...choice(TARGETS) };
  const def = { ...base, motion: "bounce" };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.15, h * 0.4);
  const vx = (fromLeft ? 1 : -1) * randRange(2.2, 3.4);
  const vy = randRange(-5, -3);
  return new Target({ def, x, y, vx, vy, speedMul: 1 });
}

// Target Chain: single target that starts mid-arena moving in a random dir.
// Always uses linear motion so we can cleanly redirect it on each hit.
export function spawnChainStart(w, h, def) {
  const angle = Math.random() * Math.PI * 2;
  const speed = 3.2;
  const t = new Target({
    def: { ...def, motion: "linear" },
    x: w / 2,
    y: h * 0.4,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    speedMul: 1,
  });
  t.hasEntered = true; // skip the "fly in from offscreen" grace period
  t.isChain = true;
  return t;
}

// Swap a chain target's appearance/size/difficulty to a new def in-place.
export function morphChainTarget(t, def) {
  if (!t) return;
  t.def = { ...def, motion: "linear" };
  t.size = def.size;
  t.motion = "linear";
  t.rotation = 0;
  t.rotSpeed = 0;
}

// Arrow Pack pickup — floats across with bouncy motion; +20 arrows on hit.
export function spawnArrowPack(w, h) {
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.6);
  const vx = (fromLeft ? 1 : -1) * randRange(2.0, 3.0);
  const vy = randRange(-4.5, -2.5);
  return new Target({
    def: { ...ARROW_PACK_TARGET }, x, y, vx, vy, speedMul: 1,
  });
}

export function spawnTrophy(w, h) {
  const def = { ...TROPHY_TARGET };
  const x = randRange(w * 0.2, w * 0.8);
  const y = randRange(h * 0.15, h * 0.45);
  const vx = randRange(2, 3.2) * (Math.random() < 0.5 ? 1 : -1);
  const vy = randRange(-5, -3);
  return new Target({ def, x, y, vx, vy, speedMul: 1 });
}

// Piñata Party: a fat 5-HP container that bounces around. On death the caller
// replaces it with a burst of small candy targets via spawnPinataBurst.
export function spawnPinata(w, h) {
  const def = { ...PINATA_TARGET };
  const x = randRange(w * 0.3, w * 0.7);
  const y = randRange(h * 0.18, h * 0.35);
  const vx = (Math.random() < 0.5 ? 1 : -1) * randRange(1.6, 2.4);
  const vy = randRange(-3, -1.5);
  const t = new Target({ def, x, y, vx, vy, speedMul: 1 });
  t.isPinata = true;
  t.hp = 5;
  t.maxHp = 5;
  return t;
}

// Burst N candy targets out from a point — short-lived, fast scatter, every
// hit counts toward the round's hit goal.
export function spawnPinataBurst(x, y, count = 8) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + randRange(-0.2, 0.2);
    const speed = randRange(3.5, 5.5);
    const base = choice(TARGETS.filter((t) => t.difficulty !== "skull"));
    const def = { ...base, motion: "linear", size: 60 };
    const t = new Target({
      def, x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      speedMul: 1,
    });
    t.canFall = true; // candy escapes off-screen if you don't snag it
    t.lifespan = 180;  // ~3s @60fps so they don't pile up
    out.push(t);
  }
  return out;
}

// Color Match: a normal target tagged green-or-red. The mode's onHit reads
// `colorState` to score (green) or penalize (red).
export function spawnColored(w, h) {
  const isGreen = Math.random() < 0.55;
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull"));
  const def = {
    ...base,
    motion: "linear",
    tintGlow: isGreen ? "#39FF14" : "#FF3B30",
    colorState: isGreen ? "green" : "red",
  };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.2, 3.4);
  return new Target({ def, x, y, vx, vy: randRange(-0.4, 0.4), speedMul: 1 });
}

// Shrinking Stars: target spawns at full size and steadily shrinks. Onhit the
// caller scales coin reward inversely with current size for "wait for tiny =
// jackpot" tension.
export function spawnShrinker(w, h) {
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 3));
  const big = Math.min(180, base.size * 1.7);
  const def = {
    ...base,
    motion: "linear",
    size: big,
    shrinkPerTick: big / 240, // shrinks to nothing over ~4s @60fps
    minSize: 24,
  };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -80 : w + 80;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(1.3, 2.0);
  return new Target({ def, x, y, vx, vy: randRange(-0.2, 0.2), speedMul: 1 });
}

// Conveyor Combo: a parade of 6 targets in formation, all moving together
// at the same speed. Caller checks order in onHit (skipping ahead resets the
// multiplier).
export function spawnConveyorLine(w, h) {
  const fromLeft = Math.random() < 0.5;
  const speed = randRange(1.8, 2.4) * (fromLeft ? 1 : -1);
  const y = randRange(h * 0.25, h * 0.55);
  const lineId = `line_${Math.random().toString(36).slice(2, 7)}`;
  const out = [];
  const count = 6;
  const spacing = 110;
  for (let i = 0; i < count; i++) {
    const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 2));
    const def = { ...base, motion: "linear", size: 80 };
    const xOffset = (count - 1 - i) * spacing;
    const x = fromLeft ? -60 - xOffset : w + 60 + xOffset;
    const t = new Target({ def, x, y, vx: speed, vy: 0, speedMul: 1 });
    t.isConveyor = true;
    t.lineId = lineId;
    t.conveyorIndex = i; // 0 = leftmost when entering from the left
    out.push(t);
  }
  return out;
}

// Mole Mash: pops up briefly in the upper screen band and ducks back. No
// wall bounce, just a short lifespan so the player has to be quick.
export function spawnMole(w, h) {
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 2));
  const def = { ...base, motion: "linear", size: 80, lifespan: 90 };
  const x = randRange(80, w - 80);
  // Spawn across the upper half of the arena (well clear of the crossbow at
  // the bottom and the HUD band at the very top) so moles read as "popping
  // up" into the player's main shooting zone.
  const y = randRange(h * 0.18, h * 0.5);
  const t = new Target({ def, x, y, vx: 0, vy: 0, speedMul: 1 });
  t.canFall = true;   // bypass the bottom-pad clamp so y stays put
  t.hasEntered = true;
  t.isMole = true;
  return t;
}

// Magnet Pull: a magnet target that lives for 3s and exerts a gravity pull
// on nearby targets. Caller drives the pull each frame and chain-pops the
// cluster on hit.
export function spawnMagnet(w, h) {
  const def = { ...MAGNET_TARGET };
  const x = randRange(w * 0.25, w * 0.75);
  const y = randRange(h * 0.25, h * 0.5);
  const vx = (Math.random() < 0.5 ? 1 : -1) * randRange(1.0, 1.6);
  const vy = randRange(-2, -1);
  const t = new Target({ def, x, y, vx, vy, speedMul: 1 });
  t.isMagnet = true;
  t.tintGlow = "#A020F0";
  return t;
}

// Splitter: enters from a side. On hit (mode-handler) the caller spawns 2
// smaller copies via spawnSplitter(_, _, gen + 1) until gen reaches 2.
export function spawnSplitter(w, h, gen = 0) {
  const SIZES = [110, 65, 40];
  const SPEEDS = [[2.0, 2.8], [3.5, 4.5], [5.0, 6.5]];
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 3));
  const def = { ...base, motion: "linear", size: SIZES[gen] };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.55);
  const [lo, hi] = SPEEDS[gen];
  const vx = (fromLeft ? 1 : -1) * randRange(lo, hi);
  const t = new Target({ def, x, y, vx, vy: randRange(-0.5, 0.5), speedMul: 1 });
  t.isSplitter = true;
  t.splitGen = gen;
  return t;
}

// Mirror Twins: spawns a vertically-mirrored pair sharing a `pairId`. Caller
// (mirror_twins onHit) checks if a pair's other twin was hit recently for a
// bonus.
export function spawnMirrorPair(w, h) {
  const fromLeft = Math.random() < 0.5;
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 2));
  const def = { ...base, motion: "linear", size: 80 };
  const y = randRange(h * 0.2, h * 0.55);
  const speed = randRange(2.4, 3.2);
  const pairId = `pair_${Math.random().toString(36).slice(2, 7)}`;
  const left = new Target({
    def: { ...def },
    x: -60, y, vx: speed, vy: 0, speedMul: 1,
  });
  left.isMirrorTwin = true;
  left.pairId = pairId;
  left.canFall = true;  // skip wall-bounce so the symmetry stays clean
  left.hasEntered = true;
  const right = new Target({
    def: { ...def },
    x: w + 60, y, vx: -speed, vy: 0, speedMul: 1,
  });
  right.isMirrorTwin = true;
  right.pairId = pairId;
  right.canFall = true;
  right.hasEntered = true;
  return [left, right];
}

// Slow-Mo Drop: lightning pickup that triggers 3s of bullet time.
export function spawnLightning(w, h) {
  const def = { ...LIGHTNING_TARGET };
  const fromLeft = Math.random() < 0.5;
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.4, 3.4);
  const t = new Target({ def, x, y, vx, vy: randRange(-0.4, 0.4), speedMul: 1 });
  t.isLightning = true;
  t.tintGlow = "#00FFFF";
  return t;
}

// Lucky Wheel: prize-wheel pickup that rolls a random buff on hit.
export function spawnLuckyWheel(w, h) {
  const def = { ...WHEEL_TARGET };
  const x = randRange(w * 0.2, w * 0.8);
  const y = randRange(h * 0.18, h * 0.4);
  const vx = (Math.random() < 0.5 ? 1 : -1) * randRange(1.4, 2.2);
  const vy = randRange(-3, -1.5);
  const t = new Target({ def, x, y, vx, vy, speedMul: 1 });
  t.isLuckyWheel = true;
  t.tintGlow = "#FFD700";
  // Visible spinning helps it read as a wheel even before the PNG loads.
  t.rotSpeed = 0.06;
  return t;
}

// Boomerang: target enters from one side and reverses direction once when it
// reaches the far edge, returning back the way it came. The return trip is
// flagged with `isReturning` so the onHit can apply a 3x reward.
export function spawnBoomerang(w, h) {
  const fromLeft = Math.random() < 0.5;
  const base = choice(TARGETS.filter((t) => t.difficulty !== "skull" && t.minLevel <= 3));
  const def = { ...base, motion: "linear" };
  const x = fromLeft ? -60 : w + 60;
  const y = randRange(h * 0.2, h * 0.55);
  const vx = (fromLeft ? 1 : -1) * randRange(2.0, 2.6);
  const t = new Target({ def, x, y, vx, vy: randRange(-0.3, 0.3), speedMul: 1 });
  t.canFall = true;       // skip the wall-bounce so it can leave then return
  t.hasEntered = true;
  t.isBoomerang = true;
  t.fromLeft = fromLeft;
  t.hasReversed = false;
  return t;
}

// Coin Storm: pure-positive falling coin pickup. +50 coins, no penalty, does
// not count toward hits-vs-goal.
export function spawnFallingCoin(w, h) {
  const def = { ...COIN_PICKUP_TARGET };
  const x = randRange(60, w - 60);
  const y = -60;
  const t = new Target({
    def, x, y,
    vx: randRange(-0.3, 0.3),
    vy: randRange(2.6, 3.8),
    speedMul: 1,
  });
  t.canFall = true;
  t.isCoinPickup = true;
  return t;
}

// Call after each boss hit — updates its behavior per remaining HP.
// Size is controlled by applyBossShrink so bosses visibly shrink with each hit.
export function updateBossPhase(boss) {
  if (!boss || !boss.isBoss) return;
  const pct = boss.hp / boss.maxHp;
  let newPhase = 1;
  if (pct <= 0.33) newPhase = 3;
  else if (pct <= 0.66) newPhase = 2;
  if (newPhase === boss.phase) return;
  boss.phase = newPhase;
  const sign = Math.sign(boss.vx) || 1;
  if (newPhase === 2) {
    boss.vx = sign * 4.0;
    boss.vy = 1.8 * (Math.random() < 0.5 ? 1 : -1);
    boss.motion = "zigzag";
    boss.baseY = boss.y;
  } else if (newPhase === 3) {
    boss.vx = sign * 5.4;
    boss.motion = "spin";
    boss.rotSpeed = 0.06 * (Math.random() < 0.5 ? -1 : 1);
  }
}

export { getImage };
